import { FormProvider, useForm } from 'react-hook-form';
import { Button, Card, Heading, Paragraph, Textfield } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { SolcellerFormData } from '../types';
import { useSubmitSolceller } from '../useSubmitSolceller';
import { FormErrors } from '../../../../form/formErrors';

import { ProductionData } from './components/productionData';
import { KwpPanels } from './components/kwpPanels';
import { ProductionCapacityRadio } from './components/productionCapacityRadio';
import { ButtonWrapper, CardContentWrapper, MainContentWrapper, StyledParagraph } from './components/styledComponents';
import { useSolcellerValidation } from './components/useSolcellerValidation';
import { KwpInfoPopOver } from './components/kwpInfoPopOver';
import { SqmPanels } from './components/sqmPanels';

interface Props {
  formData?: SolcellerFormData;
}

export const Form: FC<Props> = ({ formData }) => {
  const { t } = useTranslation();
  const { validateYear, yearPattern } = useSolcellerValidation();
  const { submitSolceller } = useSubmitSolceller();

  const methods = useForm<SolcellerFormData>({
    defaultValues: {
      ...formData,
      kwpPanels: formData?.kwpPanels || [{}],
      sqmPanels: formData?.sqmPanels || [{}]
    },
    shouldUnregister: true
  });

  const capacitySelection = methods.watch('hasCapacityData');
  const errors = methods.formState.errors;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitSolceller)} noValidate>
        <StyledParagraph data-size="xs">{t('register.solar.form.allFieldsRequired')}</StyledParagraph>
        <MainContentWrapper>
          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.header.solarPanelInfo')}
            </Heading>
            <CardContentWrapper>
              <Textfield
                inputMode="numeric"
                maxLength={4}
                label={t('register.solar.form.installationYear.label')}
                error={errors.installationYear?.message}
                {...methods.register('installationYear', {
                  validate: validateYear,
                  pattern: yearPattern
                })}
              />
              <ProductionData />
            </CardContentWrapper>
          </Card>

          <Card>
            <Heading data-size="sm" level={2}>
              {t('register.solar.form.productionCapacity.header')}
            </Heading>
            <Paragraph>{t('register.solar.form.productionCapacity.description')}</Paragraph>
            <KwpInfoPopOver />

            <CardContentWrapper>
              <ProductionCapacityRadio />
              {/* Panel inputs can be either in kWp or in kvm (m²) */}
              {capacitySelection === 'yes' && <KwpPanels />}
              {capacitySelection === 'no' && <SqmPanels />}
            </CardContentWrapper>
          </Card>
          <FormErrors errors={errors} />
          <ButtonWrapper>
            <Button variant="secondary">{t('register.solar.form.buttons.back')}</Button>
            <Button type="submit">{t('register.solar.form.buttons.continue')}</Button>
          </ButtonWrapper>
        </MainContentWrapper>
      </form>
    </FormProvider>
  );
};
